import { type Comparator, type Operator, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID, RETRIEVE_BY_QUERY } from '@inpart-io/devtools-vue';
import {
    DataType,
    FieldValueType,
    type ICriteria,
    type IFieldReference,
    type IInfoGroup,
    type IInfogroupContainer,
    type IInfogroupData,
    type IInfoGroupField,
    type ILayout,
    type ILead,
    type ILeadAssetProfile,
    type ILeadCompanyProfile,
    type ILeadConsumerHealthProfile,
    type ILeadTechnologyProfile,
    type IMandatoryField,
    type IMandatoryFieldConfiguration,
    type IPrivateFieldCustomValue,
    type ISection,
    mandatoryFieldConfigurationProperties
} from '@inpart-io/padme-iap';

import { assetTypeList } from '@/constants/asset-types.constants';
import { layoutDataTypeCriteriaAttributes } from '@/pages/platform-settings/components/layout/constants/layout-settings.constants';
import { getCriteriaList } from '@/pages/platform-settings/components/layout/services/fields-management-service';
import { store } from '@/store';
import { INFO_GROUP_MODULE } from '@/store/infogroup-module';
import { GET_INFO_GROUP_BY_TYPE } from '@/store/infogroup-module/getters';
import { MANDATORY_FIELD_CONFIGURATION_MODULE } from '@/store/mandatory-field-configuration-module';
import { RETRIEVE_MANDATORY_CONFIGURATIONS } from '@/store/mandatory-field-configuration-module/actions';

import { isInfogroupEnabled, isInfogroupFieldEnabled } from './infogroups-service';
import { isInfogroupFieldInLayout } from './layout-service';

const ASSET_TRANSIENT_ATTRIBUTE = 'leadProfileInfogroups';
interface ILeadMandatoryData extends IInfogroupContainer {
    transientFields: { [ASSET_TRANSIENT_ATTRIBUTE]: Record<string, IInfogroupData> };
}

/**
 * Retrieve the array of IMandatoryFieldConfiguration applicable for classNames with a value criteria
 */
export async function retrieveObjectMandatoryFieldConfigurations(classNames: DataType[], value: string, assetCriteria?: ICriteria[]): Promise<number[]> {
    const primaryClassName = classNames[0] === DataType.LeadProfile ? DataType.Lead : classNames[0];
    const valueComp = `${layoutDataTypeCriteriaAttributes[primaryClassName]}:${value}`;

    const operators: Operator[] = [];

    const classNamesComparators = [queryBuilder.cmp.equalTo('forClassName', primaryClassName), queryBuilder.cmp.equalTo('criteriaList', valueComp)];
    operators.push(queryBuilder.op.and(...classNamesComparators));

    if (classNames.includes(DataType.LeadProfile)) {
        const assetComparators = [queryBuilder.cmp.equalTo('forClassName', DataType.LeadProfile), queryBuilder.cmp.equalTo('criteriaList', valueComp)];

        if (assetCriteria && assetCriteria.length > 0) {
            const otherComparators: Comparator[] = [];
            for (const criterion of getCriteriaList(assetCriteria)) {
                otherComparators.push(queryBuilder.cmp.equalTo('criteriaList', criterion));
            }

            assetComparators.push(...otherComparators);
        }
        operators.push(queryBuilder.op.and(...assetComparators));
    }
    const query = queryBuilder.build(queryBuilder.op.or(...operators));

    return (
        store.dispatch(`${MANDATORY_FIELD_CONFIGURATION_MODULE}/${RETRIEVE_BY_QUERY}`, {
            query,
            properties: mandatoryFieldConfigurationProperties.DEFAULT
        }) ?? []
    );
}

/**
 * Retrieve the array of IMandatoryFieldConfiguration applicable for an existing infogroup container
 */
export async function retrieveMandatoryFieldConfigurationsForContainer(dobjId: number, module: string): Promise<number[]> {
    return (
        store.dispatch(`${module}/${RETRIEVE_MANDATORY_CONFIGURATIONS}`, {
            dobjId
        }) ?? []
    );
}

/**
 * Identify empty infogroup and infogroup fields of a given container and return the corresponding infogroup objet list with filtered fields
 */
export function getEmptyInfogroupsFromContainer(dobj: IInfogroupContainer, infogroups: IInfoGroup[]): IInfoGroup[] {
    const emptyInfogroupList: IInfoGroup[] = [];
    for (const infogroup of infogroups) {
        if (infogroup.forClassName === dobj['@type']) {
            const emptyFields = getEmptyInfogroupFields(dobj, infogroup);
            if (emptyFields.length > 0) {
                emptyInfogroupList.push(Object.assign({}, infogroup, { fields: emptyFields }));
            }
        } else if (dobj['@type'] === DataType.Lead) {
            let assetProfileEntity: ILeadAssetProfile | ILeadTechnologyProfile | ILeadConsumerHealthProfile | ILeadCompanyProfile;
            for (const type of assetTypeList) {
                // The list of infogroup from the parameters could contain infogroups for a different type of asset so a check is needed
                if (infogroup.forClassName === type.assetProfileType && (dobj as ILead).assetProfileEntity?.type === type.assetProfileType) {
                    assetProfileEntity = store.getters[`${type.assetProfileEntityModule}/${GET_BY_ID}`]((dobj as ILead).assetProfileEntity.dobjId);
                }
            }

            if (assetProfileEntity !== undefined) {
                const emptyFields = getEmptyInfogroupFields(assetProfileEntity, infogroup);
                if (emptyFields.length > 0) {
                    emptyInfogroupList.push(Object.assign({}, infogroup, { fields: emptyFields }));
                }
            }
        }
    }
    return emptyInfogroupList;
}

/**
 * Check if a given infogroup container has a value for all fields in a given infogroup
 */
function getEmptyInfogroupFields(dobj: IInfogroupContainer, infogroup: IInfoGroup): IInfoGroupField[] {
    const infogroupAttribute = dobj.infogroups?.[infogroup.attributeName];
    const emptyFields: IInfoGroupField[] = [];
    if (isInfogroupEnabled(infogroup)) {
        if (infogroupAttribute !== undefined && infogroupAttribute !== null) {
            const enabledFields = infogroup.fields.filter((infogroupField) => {
                return isInfogroupFieldEnabled(infogroupField);
            });

            for (const field of enabledFields) {
                const attributeName = field.attributeName;
                // If the field is null, undefined or an empty array
                if (
                    infogroupAttribute[attributeName] === null ||
                    infogroupAttribute[attributeName] === undefined ||
                    (Array.isArray(infogroupAttribute[attributeName]) &&
                        (infogroupAttribute[attributeName] as Array<IPrivateFieldCustomValue | number>).length === 0) ||
                    ((field.valueType === FieldValueType.TEXT || field.valueType === FieldValueType.LONG_TEXT) &&
                        (infogroupAttribute[attributeName] as string)?.trim()?.length === 0)
                ) {
                    emptyFields.push(field);
                }
            }
        } else {
            return infogroup.fields;
        }
    }
    return emptyFields;
}

/**
 *  Utility function to transform the data collected in mandatory-field-edition into an object that can be sent to create a Lead
 */
export function getLeadCreationProperties(mandatoryData: Map<IInfoGroup, IInfogroupData>): ILeadMandatoryData {
    const leadProperties: ILeadMandatoryData = {
        infogroups: {},
        transientFields: { [ASSET_TRANSIENT_ATTRIBUTE]: {} }
    };
    mandatoryData.forEach((value: IInfogroupData, key: IInfoGroup) => {
        if (key.forClassName === DataType.Lead) {
            if (!(leadProperties.infogroups as Record<string, unknown>)[key.attributeName]) {
                (leadProperties.infogroups as Record<string, unknown>)[key.attributeName] = {};
            }
            Object.assign((leadProperties.infogroups as Record<string, unknown>)[key.attributeName], value);
        } else {
            // If the target of the mandatory data is the asset profile then it is placed in the transientFields
            if (key.forClassName === DataType.LeadProfile) {
                if (!leadProperties.transientFields[ASSET_TRANSIENT_ATTRIBUTE][key.attributeName]) {
                    leadProperties.transientFields[ASSET_TRANSIENT_ATTRIBUTE][key.attributeName] = {};
                }
                Object.assign(leadProperties.transientFields[ASSET_TRANSIENT_ATTRIBUTE][key.attributeName], value);
            }
        }
    });
    return leadProperties;
}

/**
 *  Utility function to transform the data collected in mandatory-field-edition into an object that can be sent to create a Lead
 */
export function getObjectCreationProperties(mandatoryData: Map<IInfoGroup, IInfogroupData>): IInfogroupContainer {
    const objectProperties: IInfogroupContainer = {
        infogroups: {}
    };

    mandatoryData.forEach((value: IInfogroupData, key: IInfoGroup): void => {
        if (!(objectProperties.infogroups as Record<string, unknown>)[key.attributeName]) {
            (objectProperties.infogroups as Record<string, unknown>)[key.attributeName] = {};
        }
        Object.assign((objectProperties.infogroups as Record<string, unknown>)[key.attributeName], value);
    });

    return objectProperties;
}

/**
 * Gets a list of IInfogroups with only relevant fields from an array of IMandatoryFieldConfiguration
 */
export function getMandatoryInfoGroupSubset(mandatoryConfigs: IMandatoryFieldConfiguration[], layouts: ILayout[]): IInfoGroup[] {
    const relevantInfogroups: IInfoGroup[] = [];
    const fieldMap = new Map<string, string[]>();
    for (const config of mandatoryConfigs) {
        if ((config.mandatory?.length ?? 0) > 0) {
            for (const fieldDescription of config.mandatory) {
                if (fieldMap.has(fieldDescription.type)) {
                    fieldMap.set(fieldDescription.type, [...fieldMap.get(fieldDescription.type), fieldDescription.fieldKey]);
                } else {
                    fieldMap.set(fieldDescription.type, [fieldDescription.fieldKey]);
                }
            }
        }
    }
    fieldMap.forEach((value: string[], key: string) => {
        const infogroup: IInfoGroup = store.getters[`${INFO_GROUP_MODULE}/${GET_INFO_GROUP_BY_TYPE}`](key);

        // We only keep enabled infogroupFields that are mandatory
        const infogroupFields = infogroup.fields.filter((field) => {
            return value.includes(field.infogroupFieldType) && isInfogroupFieldEnabled(field) && isInfogroupFieldInLayout(field.infogroupFieldType, layouts);
        });

        if (infogroupFields?.length > 0) {
            relevantInfogroups.push(
                Object.assign({}, infogroup, {
                    fields: infogroupFields
                })
            );
        }
    });
    return relevantInfogroups;
}

/**
 * Checks if given infogroup field is in given mandatory field configuration
 */
export function isInfogroupFieldInMandatory(infogroupFieldKey: string, mandatoryConfigs: IMandatoryFieldConfiguration[]): boolean {
    for (const mandatoryConfig of mandatoryConfigs) {
        if (
            mandatoryConfig?.mandatory?.some((mandatoryField: IMandatoryField) => {
                return mandatoryField.fieldKey === infogroupFieldKey;
            })
        ) {
            return true;
        }
    }
    return false;
}

export function flattenSectionFields(sections: ISection[]): IFieldReference[] {
    return sections
        .map((section) => {
            return (
                section?.subsections?.map((subsection) => {
                    return subsection?.fields ?? [];
                }) ?? []
            );
        })
        .flat(3);
}

export function getMandatoryFieldsFromLayout(sections: ISection[], mandatoryConfigs: IMandatoryFieldConfiguration[], layouts: ILayout[]): IInfoGroup[] {
    const relevantInfoGroups: IInfoGroup[] = [];
    const mandatory: string[] = [];

    for (const config of mandatoryConfigs) {
        if ((config.mandatory?.length ?? 0) > 0) {
            config.mandatory.forEach((field) => {
                mandatory.push(field.fieldKey);
            });
        }
    }

    const filteredReferenceFields = flattenSectionFields(sections).filter((field) => {
        return mandatory.find((fieldKey) => {
            return fieldKey === field.fieldKey;
        });
    });

    filteredReferenceFields.forEach((field) => {
        const filteredIGFields: IInfoGroupField[] = [];
        const infoGroup: IInfoGroup = store.getters[`${INFO_GROUP_MODULE}/${GET_INFO_GROUP_BY_TYPE}`](field.type);

        infoGroup.fields
            .filter((igField) => {
                return isInfogroupFieldEnabled(igField) && isInfogroupFieldInLayout(igField.infogroupFieldType, layouts);
            })
            .map((igField: IInfoGroupField): IInfoGroupField[] => {
                if (field.fieldKey === igField.infogroupFieldType) {
                    filteredIGFields.push(igField);
                }

                return [];
            });

        if (infoGroup.fields?.length > 0) {
            relevantInfoGroups.push(
                Object.assign({}, infoGroup, {
                    fields: filteredIGFields
                })
            );
        }
    });

    return relevantInfoGroups;
}
